import React, { Fragment } from "react";

import styled from "styled-components/macro";

import { useFamilyRegistrationContext } from "Signups/FamilyRegistration/FamilyRegistrationContext";
import CheckboxSettingSection from "Signups/FamilyRegistration/Shared/CheckboxSettingSection";
import Dropdown from "Signups/FamilyRegistration/Shared/Dropdown";
import ErrorMessage from "Signups/FamilyRegistration/Shared/ErrorMessage";
import useFamilyRegistrationAdminPermissions from "Signups/FamilyRegistration/Shared/useFamilyRegistrationAdminPermissions";
import useUpdateSetting from "Signups/FamilyRegistration/Shared/useUpdateSetting";

import FnrButton3 from "UI/FnrButton3";
import UnoSolid from "UI/images/UnoSolid";

export default function RecurringGifts() {
  const { state } = useFamilyRegistrationContext();

  const { updateSetting, error } = useUpdateSetting();

  const {
    shouldDisableFamilyRegistrationSettings,
    shouldDisableRecurringGifts,
    shouldShowRecurringGiftsNoticeMessage,
    shouldHideFundItItems,
  } = useFamilyRegistrationAdminPermissions();

  let availableFunds =
    state?.familyRegistrationSettings?.recurringGifts?.availableFunds || [];

  const selectedFundID =
    state?.familyRegistrationSettings?.recurringGifts?.selectedFundID;

  async function onSelect(groupID) {
    await updateSetting({
      setting: "recurringGifts",
      itemID: groupID,
      value: groupID,
    });
  }

  function linkToFundIt() {
    window.fnReactBus.goToUrl("fundit");
  }

  if (shouldHideFundItItems) return null;

  return (
    <CheckboxSettingSection
      setting="recurringGifts"
      label="Invite members to set up their online giving as part of the registration process"
      disabled={
        shouldDisableFamilyRegistrationSettings || shouldDisableRecurringGifts
      }
    >
      <Explainer>
        Enabling this setting will prompt members to securely add their payment
        info and create a recurring gift{" "}
        <Link onClick={linkToFundIt}>through Flocknote</Link>.
      </Explainer>

      <GivingFundList
        availableFunds={availableFunds}
        selectedFundID={selectedFundID}
        onSelect={onSelect}
        disabled={availableFunds?.length <= 0}
      />
      {shouldShowRecurringGiftsNoticeMessage && <NoGivingFundsMessage />}
      <ErrorMessage error={error?.error} marginLeft="0px" />
    </CheckboxSettingSection>
  );
}

function GivingFundList({ availableFunds, selectedFundID, onSelect }) {
  return (
    <Fragment>
      <div style={{ margin: "16px 0 8px 0" }}>
        When members set up recurring giving, which fund should gifts go to?
      </div>
      <div style={{ maxWidth: "360px" }}>
        <Dropdown
          nameKey="fundDescription"
          idKey="fundID"
          placeholder={"Select a fund"}
          options={availableFunds}
          onSelect={onSelect}
          initialSelectedId={selectedFundID}
        />
      </div>
    </Fragment>
  );
}

function NoGivingFundsMessage() {
  return (
    <RecurringIsOffMessage>
      <h3>
        <UnoSolid color="white" size={24} />
        {`You're are almost there!`}
      </h3>
      <p>
        Recurring giving must be turned on for at least 1 fund for members to
        set up recurring donations.
      </p>
      <div className="fund-button">
        <FnrButton3
          onClick={() => window.fnReactBus.goToUrl("giving")}
          size="small"
          color="green"
        >
          Go to Giving Funds
        </FnrButton3>{" "}
        <div className="message-saved">{`Your progress will be saved.`}</div>
      </div>
    </RecurringIsOffMessage>
  );
}

const RecurringIsOffMessage = styled.div`
  max-width: 500px;
  padding: 16px;
  border-radius: 9px;
  background: linear-gradient(to bottom, #0ea3c9, 10%, #288ec3);
  color: white;
  margin-top: 20px;

  > h3 {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0;
    font-weight: 600;
  }

  > p {
    margin-top: 12px;
    margin-bottom: 0;
  }

  .fund-button {
    margin-top: 16px;
  }

  .message-saved {
    font-size: 12px;
    color: hsl(0 0% 100% / 0.8);
    margin-top: 4px;
  }
`;

const Explainer = styled.div`
  font-size: 0.9rem;
  color: #15436ab3;

  a {
    color: var(--fnBlue) !important;
  }
`;

const Link = styled.button`
  background: none;
  padding: 0;
  margin: 0;
  border: none;
  color: var(--fnBlue);
  font-size: inherit;
  font-weight: inherit;
  text-decoration: underline;

  &:hover {
    cursor: pointer;
  }
`;
